// Country codes & phone helpers — Latin digits always.
export const COUNTRIES = [
  { code: "966", flag: "🇸🇦", name: "السعودية" },
  { code: "971", flag: "🇦🇪", name: "الإمارات" },
  { code: "965", flag: "🇰🇼", name: "الكويت" },
  { code: "974", flag: "🇶🇦", name: "قطر" },
  { code: "973", flag: "🇧🇭", name: "البحرين" },
  { code: "968", flag: "🇴🇲", name: "عمان" },
  { code: "20", flag: "🇪🇬", name: "مصر" },
  { code: "962", flag: "🇯🇴", name: "الأردن" },
  { code: "961", flag: "🇱🇧", name: "لبنان" },
  { code: "963", flag: "🇸🇾", name: "سوريا" },
  { code: "964", flag: "🇮🇶", name: "العراق" },
  { code: "967", flag: "🇾🇪", name: "اليمن" },
  { code: "970", flag: "🇵🇸", name: "فلسطين" },
];

const KNOWN_CODES = COUNTRIES.map((c) => c.code);

// Extra country dial codes we accept in normalization (even if not in the picker).
const EXTRA_DIAL_CODES = ["90", "91", "92", "93", "98", "212", "213", "216", "218", "249", "1", "33", "44", "49"];
const ALL_DIAL_CODES = Array.from(new Set([...KNOWN_CODES, ...EXTRA_DIAL_CODES]));
// Longest-first for greedy matching.
ALL_DIAL_CODES.sort((a, b) => b.length - a.length);

function toLatin(input) {
  return String(input || "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());
}

/**
 * Normalise any messy phone to E.164 form ("+" + dial + local digits).
 * Handles:
 *   - Arabic-Indic digits (٠-٩) → Latin
 *   - "00" international prefix → "+"
 *   - Multiple "+" signs from bad imports ("+966+971..." → "+971...")
 *   - Concatenated codes with no separator ("+966971..." → "+971...")
 *   - Whitespace, dashes, parentheses
 */
export function normalizePhone(phone) {
  if (!phone) return "";
  let p = toLatin(phone).trim().replace(/[\s\-()]/g, "");
  // "00" prefix → "+"
  if (p.startsWith("00")) p = "+" + p.slice(2);
  // Multiple "+" — keep the last one (last-written wins).
  const pluses = (p.match(/\+/g) || []).length;
  if (pluses > 1) p = p.slice(p.lastIndexOf("+"));
  // No leading "+" but digits start with a known dial code → prepend "+".
  if (!p.startsWith("+")) {
    const digits = p.replace(/\D/g, "");
    for (const dial of ALL_DIAL_CODES) {
      if (digits.startsWith(dial) && digits.length >= dial.length + 6) {
        p = "+" + digits;
        break;
      }
    }
  }
  // Concatenated codes: "+966971XXXXXXXX" — if after the "+" we see a valid dial,
  // then another valid dial follows, drop the first (it was a wrong prefix).
  if (p.startsWith("+")) {
    const rest = p.slice(1);
    for (const first of ALL_DIAL_CODES) {
      if (!rest.startsWith(first)) continue;
      const after = rest.slice(first.length);
      // Look for a nested dial code inside the same number.
      for (const second of ALL_DIAL_CODES) {
        if (second === first) continue;
        if (after.startsWith(second) && after.length >= second.length + 6) {
          p = "+" + after;
          break;
        }
      }
      break;
    }
  }
  return p;
}

// Normalise any phone → digits only. Strips a leading 0.
export function stripToDigits(phone) {
  const normalised = normalizePhone(phone);
  const clean = (normalised || toLatin(phone)).replace(/\D/g, "");
  return clean.startsWith("0") ? clean.slice(1) : clean;
}

// Given "966501234567" or "+966 50 123 4567" → { code:"966", flag:"🇸🇦", name:"السعودية", number:"501234567" }
export function extractCountryCode(phone) {
  const clean = stripToDigits(phone);
  // Longest-prefix match wins (order matters: 4→3→2)
  for (const len of [4, 3, 2, 1]) {
    const prefix = clean.substring(0, len);
    const hit = COUNTRIES.find((c) => c.code === prefix);
    if (hit) return { ...hit, number: clean.substring(len) };
  }
  // Default to KSA
  return { ...COUNTRIES[0], number: clean };
}

// "🇸🇦 +966 501234567"
export function formatPhoneDisplay(phone) {
  if (!phone) return "";
  const { flag, code, number } = extractCountryCode(phone);
  return `${flag} +${code} ${number}`;
}

// "+966 501234567" — no flag (safe for Excel/CSV/PDF)
export function formatPhonePlain(phone) {
  if (!phone) return "";
  const { code, number } = extractCountryCode(phone);
  return `+${code} ${number}`;
}

// "966501234567" — for wa.me links.
export function formatPhoneForWhatsApp(phone) {
  const clean = stripToDigits(phone);
  const has = KNOWN_CODES.some((k) => clean.startsWith(k));
  return has ? clean : `966${clean}`;
}
